import React from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { TabTrigger } from 'src/domains/common/components/tabs/tabs';
import { mainColors } from 'src/assets/colors';
import { ComplianceConfigurationTabs as ComplianceConfigurationTabsWrapper } from 'src/components/ComplianceConfiguration/ComplianceConfiguration.style';

export type ComplianceConfigurationSubTabType =
    | 'gamstop-previously-positive'
    | 'source-of-funds'
    | 'self-exclusion'
    | 'license-config'
    | 'auto-logout'
    | 'activity-timeframe'
    | 'default-deposit-limits'
    | 'markers-of-harm-popup';

type ComplianceConfigurationTabType = {
    id: ComplianceConfigurationSubTabType;
    label: string;
    onClick: () => void;
    isVisible?: boolean;
};

export const ComplianceConfigurationTabs = observer((): JSX.Element => {
    const { routerBackoffice, universeModule } = useAppStateContext();
    const { config } = ConfigState.get(useCommon());
    const { bgColorQuaternary, txtColorTertiary } = universeModule.themeColors.header;
    const activeSubTab = routerBackoffice.getParametersString('global', 'tab.tab') ?? 'gamstop-previously-positive';

    const complianceConfigurationTabs: Array<ComplianceConfigurationTabType> = [
        {
            id: 'gamstop-previously-positive',
            label: 'Gamstop Previously Positive',
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationGamstopPreviouslyPositive();
            },
        },
        {
            id: 'source-of-funds',
            label: 'Source of Funds',
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationSourceOfFunds();
            },
        },
        {
            id: 'self-exclusion',
            label: 'Self Exclusion',
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationSelfExclusion();
            },
        },
        {
            id: 'license-config',
            label: 'EKYC Configuration',
            isVisible: config.idMission,
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationLicenseConfig();
            },
        },
        {
            id: 'auto-logout',
            label: 'Auto Logout',
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationAutoLogout();
            },
        },
        {
            id: 'activity-timeframe',
            label: 'Activity Timeframe',
            isVisible: config.activityTimeframe,
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationActivityTimeframe();
            },
        },
        {
            id: 'default-deposit-limits',
            label: 'Default Deposit Limits',
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationDefaultDepositLimits();
            },
        },
        {
            id: 'markers-of-harm-popup',
            label: 'MoH Pop-Ups',
            isVisible: config.markersOfHarm,
            onClick: (): void => {
                routerBackoffice.redirectToComplianceConfigurationMarkersOfHarmPopup();
            },
        },
    ];

    return (
        <ComplianceConfigurationTabsWrapper
            className='bg-transparent p-0 h-auto shadow-none gap-3'
            data-test='compliance-configuration-tabs'
        >
            {complianceConfigurationTabs.map((tab) => {
                if (tab.isVisible === false) {
                    return null;
                }

                const isActive = activeSubTab === tab.id;

                return (
                    <TabTrigger
                        key={tab.id}
                        active={isActive}
                        className='px-4 py-2 rounded-md border transition-all duration-200'
                        dataTest={`compliance-configuration-tab-${tab.id}`}
                        onClick={tab.onClick}
                        style={{
                            backgroundColor: isActive ? bgColorQuaternary : 'transparent',
                            borderColor: isActive ? bgColorQuaternary : mainColors.main.primary,
                            borderWidth: isActive ? '0px' : '1px',
                            color: isActive ? txtColorTertiary : mainColors.main.primary,
                        }}
                    >
                        {tab.label}
                    </TabTrigger>
                );
            })}
        </ComplianceConfigurationTabsWrapper>
    );
});
